import { Capacitor } from "@capacitor/core";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";

declare global {
  interface Window {
    MSStream?: any;
  }
}

export type AppPlatformOverride = "ios" | "android" | null;
export type AppPlatformMode = "auto" | "ios" | "android";

const APP_PLATFORM_OVERRIDE_KEY = "app_platform_override";
const isNativePlatform = Capacitor.isNativePlatform();
let currentAppPlatformOverride: AppPlatformOverride = null;

function normalizeAppPlatformOverride(
  value: string | null | undefined,
): AppPlatformOverride {
  return value === "ios" || value === "android" ? value : null;
}

const getCapacitorPlatform = () => Capacitor.getPlatform();

const getResolvedAppPlatform = () => {
  const platform = getCapacitorPlatform();

  if (
    currentAppPlatformOverride &&
    (platform === "ios" || platform === "android")
  ) {
    return currentAppPlatformOverride;
  }

  return platform;
};

export async function initAppPlatformMode() {
  if (!isNativePlatform) {
    currentAppPlatformOverride = null;
    return;
  }

  try {
    const result = await SecureStoragePlugin.get({
      key: APP_PLATFORM_OVERRIDE_KEY,
    });
    currentAppPlatformOverride = normalizeAppPlatformOverride(result.value);
  } catch {
    currentAppPlatformOverride = null;
  }
}

export function getAppPlatformMode(): AppPlatformMode {
  return currentAppPlatformOverride ?? "auto";
}

export async function setAppPlatformMode(mode: AppPlatformMode) {
  const nextOverride = mode === "auto" ? null : mode;
  currentAppPlatformOverride = nextOverride;

  if (!isNativePlatform) {
    return;
  }

  try {
    if (nextOverride) {
      await SecureStoragePlugin.set({
        key: APP_PLATFORM_OVERRIDE_KEY,
        value: nextOverride,
      });
    } else {
      await SecureStoragePlugin.remove({ key: APP_PLATFORM_OVERRIDE_KEY });
    }
  } catch {
    // Ignore storage failures and keep the in-memory value for this session.
  }
}

export async function resetAppPlatformMode() {
  await setAppPlatformMode("auto");
}

export const isIOS = (): boolean => {
  if (Capacitor.isNativePlatform()) {
    return getCapacitorPlatform() === "ios";
  }

  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

export const isAndroid = (): boolean => {
  if (Capacitor.isNativePlatform()) {
    return getCapacitorPlatform() === "android";
  }

  return /Android/.test(navigator.userAgent);
};

export const isIOSOrAndroid = (): boolean => {
  return isIOS() || isAndroid();
};

export const isTablet = (): boolean =>
  /(iPad|Android(?!.*Mobile))/i.test(navigator.userAgent);

export const isMobile = (): boolean => {
  const platform = getCapacitorPlatform();
  const userAgent = navigator.userAgent;

  // Vérifie si c'est Android mais pas une tablette
  const isAndroidMobile = /Android/.test(userAgent) && /Mobile/.test(userAgent);

  // Vérifie si c'est un appareil iOS mais pas un iPad
  const isIOSMobile = /iPhone|iPod|Windows Phone|Opera Mini|IEMobile/i.test(
    userAgent,
  );

  return isAndroidMobile || isIOSMobile || platform !== "web";
};

export const isiOSApp = (): boolean => {
  return getCapacitorPlatform() === "ios";
};

export const isAndroidApp = (): boolean => {
  return getCapacitorPlatform() === "android";
};

export const isMobileApp = (): boolean => {
  const platform = getCapacitorPlatform();
  return platform === "ios" || platform === "android";
};

export const isRenderedAsIOS = (): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  return getResolvedAppPlatform() === "ios";
};

export const isRenderedAsAndroid = (): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  return getResolvedAppPlatform() === "android";
};

export const isRenderedAsMobileApp = (): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const platform = getResolvedAppPlatform();
  return platform === "ios" || platform === "android";
};

export const isWebApp = (): boolean => {
  const platform = getCapacitorPlatform();
  const isElectron = !!(
    typeof process !== "undefined" &&
    process.versions &&
    process.versions.electron
  );
  return platform === "web" && !isElectron;
};

export const isElectronApp = (): boolean => {
  const platform = getCapacitorPlatform();
  const isElectron = !!(
    typeof process !== "undefined" &&
    process.versions &&
    process.versions.electron
  );
  return platform === "web" && isElectron;
};
